import posthog from 'posthog-js';

/**
 * How a run ended. Every run that reports `migration_started` reports exactly one
 * `migration_completed` carrying one of these, so the two are a closed funnel and the
 * endings stay separable.
 */
const MigrationRunOutcome = {
  // The run answered for itself, or its document closed at a terminal status.
  COMPLETED: 'completed',
  // Past its deadline and still open: it copied what the counts say and was then killed.
  STALLED: 'stalled',
  // No document ever appeared, so the endpoint never opened the run and nothing was copied.
  NEVER_STARTED: 'never_started',
  // Turned away before it began — bad token, bad request, unknown target coach.
  REJECTED: 'rejected',
};

/**
 * Counts blocked workouts by their block reason.
 *
 * The Events Track specifies a per-workout `migration_blocked_workout_viewed`. The grid
 * paginates at 100 rows, so a literal reading would measure paging rather than blocking —
 * the same workout reports again each time it is paged back to, and a blocked workout on
 * page 4 never reports at all. One breakdown per load answers the question those events
 * were for, which is which reasons dominate a library.
 *
 * @param {Array} workouts - The rows the list endpoint returned.
 * @returns {Object} Block reason to count, for blocked workouts only.
 */
const countByBlockReason = (workouts) => workouts.reduce((counts, { isMigratable, blockReason }) => {
  if (isMigratable) {
    return counts;
  }
  const reason = blockReason || 'UNKNOWN';
  return { ...counts, [reason]: (counts[reason] || 0) + 1 };
}, {});

const captureMigrationToolOpened = (acId) => posthog.capture('migration_tool_opened', {
  ac_id: acId,
  timestamp: new Date().toISOString(),
});

const captureMigrationCoachPairSelected = (acId, {
  sourceCoachId,
  targetCoachId,
}) => posthog.capture('migration_coach_pair_selected', {
  ac_id: acId,
  source_coach_id: sourceCoachId,
  target_coach_id: targetCoachId,
});

/*
  All three counts come off the list endpoint's own response rather than being recounted here,
  so they describe the in-scope library it returned — archived, PUBLIC and AI-generated
  workouts are already excluded server-side — and reconcile with what the grid rendered.
*/
const captureMigrationWorkoutListLoaded = (acId, {
  sourceCoachId,
  totalCount,
  migratableCount,
  blockedCount,
  loadTimeMs,
}) => posthog.capture('migration_workout_list_loaded', {
  ac_id: acId,
  source_coach_id: sourceCoachId,
  total_workouts: totalCount,
  compliant_count: migratableCount,
  blocked_count: blockedCount,
  load_time_ms: loadTimeMs,
});

const captureMigrationBlockedWorkoutsViewed = (acId, {
  sourceCoachId,
  blockedCount,
  workouts,
}) => posthog.capture('migration_blocked_workouts_viewed', {
  ac_id: acId,
  source_coach_id: sourceCoachId,
  blocked_count: blockedCount,
  by_reason: countByBlockReason(workouts),
});

const captureMigrationStarted = (acId, {
  sourceCoachId,
  targetCoachId,
  workoutCount,
}) => posthog.capture('migration_started', {
  ac_id: acId,
  source_coach_id: sourceCoachId,
  target_coach_id: targetCoachId,
  workout_count: workoutCount,
});

const captureMigrationWorkoutSucceeded = (acId, {
  sourceWorkoutId,
  newWorkoutId,
  targetCoachId,
  suffixApplied,
}) => posthog.capture('migration_workout_succeeded', {
  ac_id: acId,
  source_workout_id: sourceWorkoutId,
  new_workout_id: newWorkoutId,
  target_coach_id: targetCoachId,
  suffix_applied: suffixApplied,
});

const captureMigrationWorkoutFailed = (acId, {
  sourceWorkoutId,
  targetCoachId,
  failureReason,
}) => posthog.capture('migration_workout_failed', {
  ac_id: acId,
  source_workout_id: sourceWorkoutId,
  target_coach_id: targetCoachId,
  failure_reason: failureReason,
});

/**
 * Closes out a run. Fires once for every run that reported `migration_started`, whatever
 * the outcome, with one exception: if the operator leaves the page mid-run, both the
 * response and document paths return on unmount, so that run has a `migration_started`
 * and no `migration_completed`. Read that gap as an abandoned page, not a run that never
 * started.
 *
 * `isSuccessDetailReported` says whether `migration_workout_succeeded` accounts for the
 * successes. A run whose request answered names every workout; a run settled from its
 * document does not, because the document names failures but only counts successes — so
 * for those `success_count` here is the only record of what landed. Failures are named on
 * both paths, which is why this is about success detail and not per-workout detail at all.
 *
 * A REJECTED run also carries why: `rejection_reason` is the endpoint's message and
 * `status_code` its HTTP status. Without them the event says a run was turned away but not by
 * which check, and the endpoint logs those 4xx only to Cloud Logging, never to Sentry. Both are
 * omitted on every other outcome.
 */
const captureMigrationCompleted = (acId, {
  workoutCount,
  successCount,
  failureCount,
  durationMs,
  outcome,
  isSuccessDetailReported,
  rejectionReason,
  statusCode,
}) => posthog.capture('migration_completed', {
  ac_id: acId,
  workout_count: workoutCount,
  success_count: successCount,
  failure_count: failureCount,
  duration_ms: durationMs,
  outcome,
  success_detail_reported: isSuccessDetailReported,
  ...(rejectionReason !== undefined && { rejection_reason: rejectionReason }),
  ...(statusCode !== undefined && { status_code: statusCode }),
});

export {
  MigrationRunOutcome,
  captureMigrationToolOpened,
  captureMigrationCoachPairSelected,
  captureMigrationWorkoutListLoaded,
  captureMigrationBlockedWorkoutsViewed,
  captureMigrationStarted,
  captureMigrationWorkoutSucceeded,
  captureMigrationWorkoutFailed,
  captureMigrationCompleted,
};
